import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import BackHeader from 'components/back-header/back-header';
import Button from 'components/button/button';
import ChaptersPath from 'components/chapters-path/chapters-path';
import Flex from 'components/flex/flex';
import Page from 'components/page/page';
import TabBar from 'components/tab-bar/tab-bar';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { BiEdit } from 'react-icons/bi';
import { useParams } from 'react-router-dom';
import { SegmentEventType, track } from 'segment';
import { getChapters, getFields } from 'services/chapters';
import { getChildren, getChildrenWorkedChapters, setChildrenFields } from 'services/childrens';
import { getColorFromFieldId } from 'utils/colors';
import ParentFieldList from 'views/children/chapters/parent-field-list';
import SelectFields from 'views/children/chapters/select-fields';

/* eslint-disable-next-line */
export interface ChildrenChaptersProps {}

export function ChildrenChapters(props: ChildrenChaptersProps) {
  const { id } = useParams();
  const [edit, setEdit] = useState(false);
  const [selectedFields, setSelectedFields] = useState<number[]>([]);
  const queryClient = useQueryClient();
  const setChildrenFieldsMutation = useMutation({
    mutationFn: setChildrenFields,
    onSuccess() {
      setEdit(false);
      toast('Les matières ont éte mises à jour !', {
        icon: '✅',
        duration: 2000,
      });
      queryClient.invalidateQueries({ queryKey: ['children', id] });
      track(SegmentEventType.SELECT_FIELD);
    },
  });

  const [{ data: child }, { data: fields }, { data: chapters }, { data: dataWorkedChapters }] = useQueries({
    queries: [
      {
        queryKey: ['children', id],
        queryFn: () => getChildren(id ?? ''),
      },
      {
        queryKey: ['fields'],
        queryFn: getFields,
        staleTime: 24 * 3600000 * 21,
        gcTime: 24 * 3600000 * 21,
      },
      {
        queryKey: ['chapters'],
        queryFn: getChapters,
        staleTime: 24 * 3600000 * 21,
        gcTime: 24 * 3600000 * 21,
      },
      {
        queryKey: ['workedChapters', id],
        queryFn: () => getChildrenWorkedChapters(id ?? ''),
      },
    ],
  });
  const resetSelectedFields = () => {
    if (!child?.fields) return;
    if (child?.fields.length === 0) return;
    setSelectedFields(child.fields.map((f) => f.id));
  };

  useEffect(() => {
    resetSelectedFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child]);
  const allFields = useMemo(
    () =>
      chapters
        ?.filter((chapter) => chapter.cycles.find((cycle) => cycle.id === child?.cycle?.id))
        .map((chapter) => chapter.field)
        ?.filter((field, index, self) => self.findIndex((f) => f.id === field.id) === index),
    [chapters, child],
  );

  const enabledFields = useMemo(
    () => allFields?.filter((f) => selectedFields.includes(f.id)),
    [selectedFields, allFields],
  );
  const disabledFields = useMemo(
    () => allFields?.filter((f) => !selectedFields.includes(f.id)),
    [selectedFields, allFields],
  );
  const [selectedFieldId, setSelectedFieldId] = useState<number>();

  return (
    <Page parentMode>
      <BackHeader
        backgroundColor={selectedFieldId ? getColorFromFieldId(selectedFieldId) : undefined}
        onClick={() => {
          setSelectedFieldId(undefined);
        }}
        hideButton={!selectedFieldId}
        action={
          edit ? null : (
            <Button
              onClick={() => {
                setEdit(true);
              }}
              color="orange"
            >
              <BiEdit />
            </Button>
          )
        }
        title={`Les matières de ${child?.firstname ?? 'votre enfant'}`}
      />
      <Flex direction="column" justify="start" align="center" width="100%" height="100%">
        {(!selectedFieldId || edit) && (
          <Flex height="100%" width="100%" style={{ overflowY: 'auto', paddingTop: 68 }}>
            {edit && (
              <SelectFields
                onCancel={() => {
                  setEdit(false);
                  resetSelectedFields();
                }}
                onSelect={() => {
                  if (!child) return;
                  setChildrenFieldsMutation.mutate({
                    id: child.id,
                    fields: selectedFields,
                  });
                }}
                selectLoading={setChildrenFieldsMutation.isPending}
                selectedFields={selectedFields}
                setSelectedFields={!setChildrenFieldsMutation.isPending ? setSelectedFields : () => {}}
                chapters={chapters ?? []}
                child={child}
                fields={fields ?? []}
              />
            )}
            {!selectedFieldId && !edit && (
              <ParentFieldList
                chapters={chapters ?? []}
                disabledFields={disabledFields ?? []}
                enabledFields={enabledFields ?? []}
                workedChapters={dataWorkedChapters ?? []}
                onSelect={setSelectedFieldId}
              />
            )}
          </Flex>
        )}
        {!!child && selectedFieldId && !edit && (
          <ChaptersPath
            allFields={allFields ?? []}
            chapters={chapters ?? []}
            child={child}
            selectedFieldId={selectedFieldId}
            workedChapters={dataWorkedChapters ?? []}
            isParent
          />
        )}

        <TabBar childrenId={id ?? ''} />
      </Flex>
    </Page>
  );
}

export default ChildrenChapters;
